// Fixed PDP bar: in-flow spacers + min-height locks preserve layout when controls
// go position:fixed (product__info-container uses flex + order). Near the footer,
// only visibility is toggled (fixedOnScroll--concealed).
let rafId = null;
let footerConcealed = false;
let stickyActive = false;
let stickyExiting = false;
let exitTimeout = null;
let rechargeLayoutStable = false;
let stabilityTimer = null;
let rechargeResizeObserver = null;
let activationLocked = false;
let stickyEntering = false;
let enterFinishTimeout = null;

const STICKY_ANIMATION_MS = 240;
const MOBILE_STICKY_MQ = '(max-width: 990px)';
const ANIMATING_IN_CLASS = 'fixedOnScroll--animating-in';
const ANIMATING_OUT_CLASS = 'fixedOnScroll--animating-out';
const RECHARGE_STABLE_MS = 250;
const VISIBLE_CLASS = 'fixedOnScroll--visible';

const placeholders = new WeakMap();
const lockedElements = [];

const FLOW_ELEMENT_KEYS = ['productTitle', 'rechargeWidget', 'addToCartButton'];

const isMobileSticky = () => window.matchMedia(MOBILE_STICKY_MQ).matches;

const barBottomReserve = () => {
  const w = window.innerWidth;
  if (w <= 990) return 300;
  if (w <= 1220) return 185;
  return 150;
};

const stickyAnimationMs = () => {
  if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return 0;
  return STICKY_ANIMATION_MS;
};

const getScrollThresholds = () => {
  const viewportHeight = window.innerHeight;

  if (isMobileSticky()) {
    return {
      activate: viewportHeight * 2.5,
      deactivate: viewportHeight * 2.35,
      bottomOffset: 900, // px from the end of the page where the bar exits
    };
  }

  return {
    activate: viewportHeight * 1.5,
    deactivate: viewportHeight * 1.4,
    bottomOffset: 800, // px from the end of the page where the bar exits
  };
};

const getStickyElements = (refs) => [refs.container, refs.addToCartButton, refs.productTitle];

const measureOccupiedHeight = (el) => {
  const cs = getComputedStyle(el);
  const marginTop = parseFloat(cs.marginTop) || 0;
  const marginBottom = parseFloat(cs.marginBottom) || 0;
  return el.getBoundingClientRect().height + marginTop + marginBottom;
};

const lockElementHeight = (el) => {
  if (!el || lockedElements.includes(el)) return;
  el.style.minHeight = `${measureOccupiedHeight(el)}px`;
  lockedElements.push(el);
};

const unlockLayout = () => {
  for (const el of lockedElements) {
    el.style.minHeight = '';
  }
  lockedElements.length = 0;
};

const ensurePlaceholder = (el) => {
  if (!el || placeholders.has(el)) return;

  const cs = getComputedStyle(el);
  const placeholder = document.createElement('div');
  placeholder.className = 'fixedOnScroll-placeholder';
  placeholder.setAttribute('aria-hidden', 'true');
  placeholder.style.height = `${measureOccupiedHeight(el)}px`;
  placeholder.style.order = cs.order;
  placeholder.style.flexShrink = '0';
  placeholder.style.width = '100%';
  el.parentNode?.insertBefore(placeholder, el);
  placeholders.set(el, placeholder);
};

const removePlaceholder = (el) => {
  const placeholder = placeholders.get(el);
  if (!placeholder) return;
  placeholder.remove();
  placeholders.delete(el);
};

const ensurePlaceholders = (refs) => {
  for (const key of FLOW_ELEMENT_KEYS) {
    ensurePlaceholder(refs[key]);
  }
};

const removePlaceholders = (refs) => {
  for (const key of FLOW_ELEMENT_KEYS) {
    removePlaceholder(refs[key]);
  }
};

const forceReflow = (refs) => {
  void refs.container?.offsetHeight;
  void refs.addToCartButton?.offsetHeight;
  void refs.productTitle?.offsetHeight;
};

const correctScroll = (scrollY) => {
  if (Math.abs(window.scrollY - scrollY) > 1) {
    window.scrollTo(0, scrollY);
  }
};

const setStickyVisible = (refs, visible) => {
  for (const el of getStickyElements(refs)) {
    el.classList.toggle(VISIBLE_CLASS, visible);
  }
};

const setMotionClass = (refs, className, on) => {
  for (const el of getStickyElements(refs)) {
    el.classList.toggle(className, on);
  }
};

const clearRechargeWidgetExitStyles = (widget) => {
  if (!widget) return;
  widget.style.removeProperty('opacity');
  widget.style.removeProperty('pointer-events');
};

const hideRechargeWidgetOnMobileExit = (widget) => {
  if (!widget) return;
  widget.style.opacity = '0';
  widget.style.pointerEvents = 'none';
};

const setConcealed = (elements, concealed) => {
  for (const el of elements) {
    if (!el) continue;
    el.classList.toggle('fixedOnScroll--concealed', concealed);
  }
};

const applyFixedClasses = (on, refs) => {
  const { container, addToCartButton, productTitle, rechargeWidget } = refs;

  clearRechargeWidgetExitStyles(rechargeWidget);

  if (on) {
    container.classList.add('fixedOnScroll');
    addToCartButton.classList.add('fixedOnScrollATC');
    productTitle.classList.add('fixedOnScrollPT');
    // iOS: the recharge selector (.rc-widget__root) is position:fixed inside the
    // shadow DOM. On iOS Safari a fixed element in shadow DOM is not pinned to the
    // viewport once its host scrolls away, so it isn't painted (selectors go blank).
    // Making the host itself position:fixed keeps iOS painting the shadow content.
    rechargeWidget.classList.add('fixedOnScrollRecharge');
  } else {
    setConcealed(getStickyElements(refs), false);
    for (const el of getStickyElements(refs)) {
      el.classList.remove(
        VISIBLE_CLASS,
        ANIMATING_IN_CLASS,
        ANIMATING_OUT_CLASS
      );
    }
    container.classList.remove('fixedOnScroll');
    addToCartButton.classList.remove('fixedOnScrollATC');
    productTitle.classList.remove('fixedOnScrollPT');
    rechargeWidget.classList.remove('fixedOnScrollRecharge');
    footerConcealed = false;
  }
};

const lockLayout = (refs) => {
  const infoContainer = document.querySelector('.product__info-container');
  const formContainer = refs.addToCartButton?.closest('.productFormContainer');

  lockElementHeight(infoContainer);
  lockElementHeight(formContainer);
};

const finishDeactivate = (refs) => {
  clearTimeout(enterFinishTimeout);
  clearTimeout(exitTimeout);
  enterFinishTimeout = null;
  exitTimeout = null;
  stickyEntering = false;

  applyFixedClasses(false, refs);
  removePlaceholders(refs);
  unlockLayout();
  stickyActive = false;
  stickyExiting = false;
  activationLocked = false;
};

const finishEnterAnimation = (refs) => {
  if (!stickyEntering) return;

  clearTimeout(enterFinishTimeout);
  enterFinishTimeout = null;
  setStickyVisible(refs, true);
  setMotionClass(refs, ANIMATING_IN_CLASS, false);
  stickyEntering = false;
  activationLocked = false;
};

const startEnterAnimation = (refs) => {
  const duration = stickyAnimationMs();

  if (duration === 0) {
    setStickyVisible(refs, true);
    activationLocked = false;
    return;
  }

  stickyEntering = true;
  setMotionClass(refs, ANIMATING_IN_CLASS, true);

  enterFinishTimeout = setTimeout(() => {
    finishEnterAnimation(refs);
  }, duration + 32);
};

const startExitAnimation = (refs) => {
  const duration = stickyAnimationMs();

  if (duration === 0) {
    finishDeactivate(refs);
    return;
  }

  setStickyVisible(refs, false);

  if (isMobileSticky()) {
    hideRechargeWidgetOnMobileExit(refs.rechargeWidget);
    refs.productTitle.classList.add(ANIMATING_OUT_CLASS);
    refs.addToCartButton.classList.add(ANIMATING_OUT_CLASS);
  } else {
    setMotionClass(refs, ANIMATING_OUT_CLASS, true);
  }

  stickyExiting = true;

  exitTimeout = setTimeout(() => {
    finishDeactivate(refs);
  }, duration + 32);
};

const setFixedOn = (on, refs) => {
  if (on) {
    if (stickyExiting || stickyEntering) return;

    const scrollY = window.scrollY;
    activationLocked = true;

    lockLayout(refs);
    ensurePlaceholders(refs);
    applyFixedClasses(true, refs);
    forceReflow(refs);
    correctScroll(scrollY);

    stickyActive = true;
    startEnterAnimation(refs);
    return;
  }

  if (!stickyActive || stickyExiting) return;

  if (stickyEntering) {
    finishDeactivate(refs);
    return;
  }

  startExitAnimation(refs);
};

const getRefs = () => {
  const widget = document.querySelector('recharge-subscription-widget');
  if (!widget?.shadowRoot) return null;

  const container = widget.shadowRoot.querySelector('.rc-widget__root');
  const addToCartButton = document.querySelector('.product-form__buttons');
  const productTitle = document.querySelector('.product__title');
  const rechargeWidget = document.querySelector('.recharge-subscription-widget');

  if (!container || !addToCartButton || !productTitle || !rechargeWidget) return null;

  return { container, addToCartButton, productTitle, rechargeWidget };
};

const isRechargeStyled = () => {
  const widget = document.querySelector('recharge-subscription-widget');
  const shadow = widget?.shadowRoot;
  if (!shadow?.querySelector('.rc-purchase-option')) return false;

  for (const styleEl of shadow.querySelectorAll('style')) {
    if (styleEl.textContent?.includes('.fixedOnScroll')) return true;
  }

  return false;
};

const markRechargeUnstable = () => {
  if (activationLocked || stickyActive || stickyExiting || stickyEntering) return;

  rechargeLayoutStable = false;
  clearTimeout(stabilityTimer);
  stabilityTimer = setTimeout(() => {
    rechargeLayoutStable = true;
    scheduleTick();
  }, RECHARGE_STABLE_MS);
};

const observeRechargeLayout = (widget) => {
  if (rechargeResizeObserver) return;

  rechargeResizeObserver = new ResizeObserver(() => {
    markRechargeUnstable();
  });
  rechargeResizeObserver.observe(widget);
  markRechargeUnstable();
};

const tick = () => {
  rafId = null;

  const refs = getRefs();
  if (!refs || !rechargeLayoutStable || !isRechargeStyled()) return;

  if (stickyExiting || stickyEntering) return;

  const all = getStickyElements(refs);
  const scrollPosition = window.scrollY;
  const { activate, deactivate, bottomOffset } = getScrollThresholds();

  const docHeight = document.documentElement.scrollHeight;
  const distanceFromBottom = docHeight - (scrollPosition + window.innerHeight);
  const nearBottom = distanceFromBottom <= bottomOffset;

  if (stickyActive) {
    if (scrollPosition <= deactivate || nearBottom) setFixedOn(false, refs);
  } else if (!activationLocked && scrollPosition > activate && !nearBottom) {
    setFixedOn(true, refs);
  }

  if (!stickyActive || stickyExiting) return;

  const viewportHeight = window.innerHeight;
  const footer = document.querySelector('footer.gk-footer');
  const footerTop = footer?.getBoundingClientRect().top ?? Number.POSITIVE_INFINITY;
  const reserve = barBottomReserve();
  const hideLine = viewportHeight - reserve - 48;
  const showLine = viewportHeight - reserve + 140;

  if (footerConcealed) {
    if (footerTop > showLine) footerConcealed = false;
  } else if (footerTop < hideLine) {
    footerConcealed = true;
  }

  setConcealed(all, footerConcealed);
};

const scheduleTick = () => {
  if (rafId != null) return;
  rafId = requestAnimationFrame(tick);
};

const initStickyWidget = () => {
  const widget = document.querySelector('recharge-subscription-widget');
  if (!widget?.shadowRoot?.querySelector('.rc-purchase-option')) return false;

  observeRechargeLayout(widget);
  tick();
  return true;
};

window.addEventListener('scroll', scheduleTick, { passive: true });
window.addEventListener('resize', scheduleTick, { passive: true });
document.addEventListener('recharge-pdp:content-updated', markRechargeUnstable);

const scrollFixedWidget = setInterval(() => {
  if (initStickyWidget()) {
    clearInterval(scrollFixedWidget);
  }
}, 300);
